import { useMutation, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import { getEmployeeUseCases } from '@personnel/composition';
import type { EmployeeDto, EmployeeUpdateDto } from '@personnel/application/dtos/EmployeeResponseDto';
import { ROLES_QUERY_KEYS } from './useRoleList';

/**
 * Hook for updating an existing employee
 *
 * Uses React Query mutation to handle the employee update flow.
 * Automatically invalidates the employees list cache on success.
 * The closing toast is the caller's: its wording depends on the save that was asked for.
 *
 * @returns {UseMutationResult} React Query mutation result
 */
export const useUpdateEmployee = () => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const { update } = getEmployeeUseCases();

  return useMutation<EmployeeDto, Error, EmployeeUpdateDto>(
    async (dto: EmployeeUpdateDto) => {
      return update.execute(dto);
    },
    {
      onSuccess: (_, dto) => {
        // Invalidate employees list to refresh the table
        queryClient.invalidateQueries(['personnel', 'employees', 'list']);
        // Invalidate employee detail cache to ensure fresh data on next edit
        queryClient.invalidateQueries(['personnel', 'employees', 'detail', dto.userUuid]);
        // Invalidate roles list to refresh usersCount (important when role changes)
        queryClient.invalidateQueries(ROLES_QUERY_KEYS.list());

        history.push('/employees');
      },
    },
  );
};
